/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { formatAmount, formatDay } from '../../utils/cruiseUtils';

/**
 * Fare summary from the price-check invoice. Every block renders only when
 * the backend sent the numbers behind it.
 */
function CruiseFareSummary({ invoice, guestsLabel, pgCharges, children }) {
  if (!invoice) return null;
  const hasAgentBlock = invoice.netPayable !== null;

  return (
    <div className="cr-panel cr-fare">
      <div className="cr-fare__head">Fare summary</div>
      <div className="cr-fare__body">
        <div className="cr-kv__row">
          <span>Cruise fare ({guestsLabel})</span>
          <b>{formatAmount(invoice.base)}</b>
        </div>
        <div className="cr-kv__row">
          <span>Taxes, fees &amp; port charges</span>
          <b>{formatAmount(invoice.tax)}</b>
        </div>
        {invoice.govTax > 0 && (
          <div className="cr-kv__row">
            <span>Government taxes (not in fare)</span>
            <b>{formatAmount(invoice.govTax)}</b>
          </div>
        )}
        <div className="cr-kv__row cr-kv__row--total">
          <b>Total amount</b>
          <b className="cr-amount cr-amount--md">
            {formatAmount(invoice.total)}
          </b>
        </div>
        {!hasAgentBlock && invoice.ncf !== null && (
          <div className="cr-kv__row cr-muted-xs">
            <span>Non-commissionable fare</span>
            <span>{formatAmount(invoice.ncf)}</span>
          </div>
        )}
        {invoice.promos.length > 0 && (
          <div className="cr-promos">
            Cruise line promos applied
            {invoice.promos.map((p) => (
              <span className="cr-tag" key={p}>
                {p}
              </span>
            ))}
          </div>
        )}
      </div>

      {hasAgentBlock && (
        <div className="cr-fare__agent">
          <b>Agent pricing</b>
          <div className="cr-kv__row">
            <span>Gross fare</span>
            <b>{formatAmount(invoice.total)}</b>
          </div>
          {invoice.commission !== null && (
            <div className="cr-kv__row cr-text-success">
              <span>Commission</span>
              <b>− {formatAmount(invoice.commission)}</b>
            </div>
          )}
          {invoice.ncf !== null && (
            <div className="cr-kv__row cr-muted-xs">
              <span>Non-commissionable fare</span>
              <span>{formatAmount(invoice.ncf)}</span>
            </div>
          )}
          <div className="cr-kv__row cr-kv__row--total">
            <b>Net payable by you</b>
            <b className="cr-amount cr-amount--sm">
              {formatAmount(invoice.netPayable)}
            </b>
          </div>
        </div>
      )}

      {pgCharges && (pgCharges.charges > 0 || pgCharges.gst > 0) && (
        <div className="cr-fare__section">
          {pgCharges.charges > 0 && (
            <div className="cr-kv__row">
              <span>Payment gateway charges</span>
              <b>{formatAmount(pgCharges.charges)}</b>
            </div>
          )}
          {pgCharges.gst > 0 && (
            <div className="cr-kv__row">
              <span>GST on gateway charges</span>
              <b>{formatAmount(pgCharges.gst)}</b>
            </div>
          )}
        </div>
      )}

      {invoice.schedule.length > 0 && (
        <div className="cr-fare__section">
          <b>Payment schedule</b>
          {invoice.schedule.map((p) => (
            <div
              className="cr-kv__row cr-muted-row"
              key={`${p.paymentCode}-${p.dueDate}`}
            >
              <span>
                {p.paymentName || p.paymentCode}
                {p.dueDate ? ` · due ${formatDay(p.dueDate)}` : ''}
              </span>
              <b>{formatAmount(p.amount)}</b>
            </div>
          ))}
        </div>
      )}

      {children && <div className="cr-fare__actions">{children}</div>}
    </div>
  );
}

CruiseFareSummary.propTypes = {
  invoice: PropTypes.object,
  guestsLabel: PropTypes.string.isRequired,
  pgCharges: PropTypes.object,
  children: PropTypes.node,
};

CruiseFareSummary.defaultProps = {
  invoice: null,
  pgCharges: null,
  children: null,
};

export default CruiseFareSummary;
